import type { DocNode } from '@atlaskit/adf-schema';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	AdfParser,
	AdfRepairStrategy,
	AdfSchemaValidator,
	AdfParseError,
} from '@atlassian/adf-parser';
import type { AIPlugin } from '@atlassian/editor-plugin-ai';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator';

import type {
	CreateCommandArrivalDebug,
	CreateStreamDebug,
	StreamErrorCallback,
	StreamRepairCallback,
} from '../command-handlers/command-handler';
import type { ContentLocation } from '../types/content';
import { log } from '../utils/aifc-logging';

const DOC_ENVELOPE_PREFIX = '{"type":"doc"';

function firstNonWhitespaceChar(value: string | undefined): string | null {
	if (!value) {
		return null;
	}

	return value.trimStart().charAt(0) || null;
}

function startsWithDocEnvelope(value: string | undefined): boolean {
	return Boolean(value?.trimStart().startsWith(DOC_ENVELOPE_PREFIX));
}

/**
 * Build a shape-only debug descriptor for a create-stream chunk.
 *
 * IMPORTANT: never include the raw `chunk` or `accumulatedBefore` values in the
 * returned object — only lengths, booleans and the first structural token.
 */
function describeCreateChunk({
	actionId,
	traceId,
	chunk,
	chunkIndex,
	accumulatedBefore,
	isFinished,
	parserPhase,
}: {
	accumulatedBefore: string;
	actionId?: string;
	chunk: string;
	chunkIndex: number;
	isFinished: boolean;
	parserPhase: CreateStreamDebug['parserPhase'];
	traceId?: string;
}): CreateStreamDebug {
	const chunkFirstToken = firstNonWhitespaceChar(chunk);
	const accumulatedFirstToken = firstNonWhitespaceChar(accumulatedBefore);

	return {
		streamDebugVersion: 2,
		streamType: 'create',
		parserPhase,
		actionId,
		traceId,
		chunkIndex,
		chunkLength: chunk.length,
		chunkFirstNonWhitespaceChar: chunkFirstToken,
		chunkStartsWithComma: chunkFirstToken === ',',
		chunkStartsWithDocEnvelope: startsWithDocEnvelope(chunk),
		isFinished,
		accumulatedLengthBefore: accumulatedBefore.length,
		accumulatedFirstNonWhitespaceCharBefore: accumulatedFirstToken,
		accumulatedStartsWithDocEnvelopeBefore: startsWithDocEnvelope(accumulatedBefore),
		accumulatedWasEmptyBefore: accumulatedBefore.length === 0,
	};
}

function withError(debug: CreateStreamDebug, error: unknown): CreateStreamDebug {
	return {
		...debug,
		errorName: error instanceof Error ? error.name : undefined,
		errorMessage: error instanceof Error ? error.message : String(error),
	};
}

/**
 * Shape-only descriptor of a `previousAccumulatedData` replay.
 *
 * This replay path runs when the user closes and reopens the Smart Create modal
 * mid-stream: the previously accumulated ADF is re-streamed as the first chunk.
 * It is a prime suspect for blank-page cases, so we record (shape only) whether
 * a replay happened and what the replayed buffer looked like.
 */
type PreviousAccumulatedDataReplay = {
	firstNonWhitespaceChar: string | null;
	length: number;
};

function withReplay(
	debug: CreateStreamDebug,
	replay: PreviousAccumulatedDataReplay | undefined,
): CreateStreamDebug {
	if (!replay) {
		return debug;
	}

	return {
		...debug,
		previousAccumulatedDataWasReplayed: true,
		previousAccumulatedDataLength: replay.length,
		previousAccumulatedDataFirstNonWhitespaceChar: replay.firstNonWhitespaceChar,
	};
}

/**
 * Accumulates shape-only, whole-stream lifecycle facts across all chunks of a
 * single create stream. Unlike the per-chunk descriptor, these answer "what
 * happened over the lifetime of the stream" — which is what is needed to
 * distinguish blank-page root causes (missing fragment 0, mid-stream reset,
 * replay bug, receiver readiness) rather than just the failure shape at finish.
 */
type StreamLifecycle = {
	firstNonEmptyChunkFirstChar?: string | null;
	firstNonEmptyChunkIndex?: number;
	firstNonEmptyChunkStartsWithComma?: boolean;
	firstNonEmptyChunkStartsWithDocEnvelope?: boolean;
	firstParserErrorChunkIndex?: number;
	firstParserErrorMessage?: string;
	processorWasResetDuringStream: boolean;
	receivedChunkCount: number;
	sawCommaPrefixedChunkBeforeDocEnvelope: boolean;
	sawDocEnvelopeChunk: boolean;
};

function createStreamLifecycle(): StreamLifecycle {
	return {
		receivedChunkCount: 0,
		sawDocEnvelopeChunk: false,
		sawCommaPrefixedChunkBeforeDocEnvelope: false,
		processorWasResetDuringStream: false,
	};
}

function withLifecycle(
	debug: CreateStreamDebug,
	lifecycle: StreamLifecycle,
	arrival: CreateCommandArrivalDebug | undefined,
): CreateStreamDebug {
	return {
		...debug,
		receivedChunkCount: lifecycle.receivedChunkCount,
		firstNonEmptyChunkIndex: lifecycle.firstNonEmptyChunkIndex,
		firstNonEmptyChunkFirstChar: lifecycle.firstNonEmptyChunkFirstChar,
		firstNonEmptyChunkStartsWithDocEnvelope: lifecycle.firstNonEmptyChunkStartsWithDocEnvelope,
		firstNonEmptyChunkStartsWithComma: lifecycle.firstNonEmptyChunkStartsWithComma,
		sawDocEnvelopeChunk: lifecycle.sawDocEnvelopeChunk,
		sawCommaPrefixedChunkBeforeDocEnvelope: lifecycle.sawCommaPrefixedChunkBeforeDocEnvelope,
		firstParserErrorChunkIndex: lifecycle.firstParserErrorChunkIndex,
		firstParserErrorMessage: lifecycle.firstParserErrorMessage,
		processorWasResetDuringStream: lifecycle.processorWasResetDuringStream,
		...(arrival
			? {
					processorWasInitializedWhenCommandArrived:
						arrival.processorWasInitializedWhenCommandArrived,
					previousAccumulatedDataPresent: arrival.previousAccumulatedDataPresent,
					previousAccumulatedDataStartsWithDocEnvelope:
						arrival.previousAccumulatedDataStartsWithDocEnvelope,
				}
			: {}),
	};
}

export class DocumentCreateCommandProcessor {
	private editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>;
	private contentLocation: ContentLocation;
	private actionId: string | undefined;
	private accumulated: string = '';
	private parser: AdfParser | undefined;
	private traceId: string | undefined;
	private chunkIndex = 0;
	private lastCreateStreamDebug: CreateStreamDebug | undefined;
	private previousAccumulatedDataReplay: PreviousAccumulatedDataReplay | undefined;
	private lifecycle: StreamLifecycle = createStreamLifecycle();
	private commandArrival: CreateCommandArrivalDebug | undefined;
	private isAborted: boolean = false;

	constructor(
		editorApi: PublicPluginAPI<[AIPlugin, AiStreamingOrchestratorPlugin]>,
		contentLocation: ContentLocation,
	) {
		this.editorApi = editorApi;
		this.contentLocation = contentLocation;
	}

	private init(traceId?: string, singleInstrumentationId?: string) {
		this.isAborted = false;
		this.editorApi.aiStreamingOrchestrator?.actions.startSession(traceId, {
			singleInstrumentationId,
			// This processor creates the initial content, so mark it a create stream to
			// suppress Post Stream Review. Subsequent update streams open PSR as normal.
			isCreateSession: true,
		});

		this.actionId = this.editorApi.aiStreamingOrchestrator?.actions.initAction(
			{
				type: 'appendNode',
				streamingType: 'create',
			},
			{
				/**
				 * Always hide the loading screen as staging area always shows Rovo conversation
				 * and will already have a loading indicator.
				 */
				hideLoadingScreen: this.contentLocation === 'staging-area',
			},
		);
		if (!this.actionId) {
			throw new Error('Failed to initialize streaming action for create');
		}

		const schema = this.editorApi.core.sharedState.currentState()?.schema;
		if (schema) {
			this.parser = new AdfParser({
				schemaValidator: new AdfSchemaValidator(schema),
				repairStrategy: new AdfRepairStrategy(schema),
			});
		}

		log('Initialized editor streaming action', { streamType: 'create', actionId: this.actionId });
	}

	isInitialized(): boolean {
		return !!this.actionId;
	}

	/**
	 * Record (shape only) that the next `stream()` call is a
	 * `previousAccumulatedData` replay — i.e. the user closed and reopened the
	 * Smart Create modal mid-stream and the previously accumulated ADF is being
	 * re-streamed. Used purely for blank-page debug telemetry; no-op unless the
	 * debug feature gate is enabled. The raw data is never stored.
	 */
	markPreviousAccumulatedDataReplay(previousAccumulatedData: string): void {
		if (!fg('platform_editor_aifc_create_stream_debug')) {
			return;
		}

		this.previousAccumulatedDataReplay = {
			length: previousAccumulatedData.length,
			firstNonWhitespaceChar: firstNonWhitespaceChar(previousAccumulatedData),
		};
	}

	/**
	 * Record (shape only) the receiver lifecycle facts known at the moment a
	 * create command arrives at the handler: whether the processor was already
	 * initialized, and whether/what shape of previousAccumulatedData the payload
	 * carried. Used purely for blank-page debug telemetry; no-op unless the
	 * debug feature gate is enabled. The raw data is never stored.
	 */
	markCommandArrived(arrival: CreateCommandArrivalDebug): void {
		if (!fg('platform_editor_aifc_create_stream_debug')) {
			return;
		}

		// Only record the first arrival of a stream; subsequent chunks for the
		// same stream re-enter process() but the lifecycle anchor is the first
		// command.
		if (!this.commandArrival) {
			this.commandArrival = arrival;
		}
	}

	/**
	 * Update the whole-stream lifecycle accumulator for a received chunk
	 * (shape only). Records first-non-empty-chunk facts, doc-envelope/comma
	 * ordering, a non-empty chunk counter, and detects a mid-stream reset
	 * (a doc-envelope chunk seen after content had already accumulated).
	 */
	private updateLifecycleForChunk(chunk: string, chunkIndex: number): void {
		const firstChar = firstNonWhitespaceChar(chunk);
		const isDocEnvelope = startsWithDocEnvelope(chunk);
		const isComma = firstChar === ',';

		this.lifecycle.receivedChunkCount += 1;

		if (this.lifecycle.firstNonEmptyChunkIndex === undefined) {
			this.lifecycle.firstNonEmptyChunkIndex = chunkIndex;
			this.lifecycle.firstNonEmptyChunkFirstChar = firstChar;
			this.lifecycle.firstNonEmptyChunkStartsWithDocEnvelope = isDocEnvelope;
			this.lifecycle.firstNonEmptyChunkStartsWithComma = isComma;
		}

		if (isDocEnvelope) {
			// A doc-envelope chunk arriving after content already accumulated
			// indicates the accumulator/stream was reset mid-stream.
			if (this.accumulated.length > 0) {
				this.lifecycle.processorWasResetDuringStream = true;
			}
			this.lifecycle.sawDocEnvelopeChunk = true;
		} else if (isComma && !this.lifecycle.sawDocEnvelopeChunk) {
			this.lifecycle.sawCommaPrefixedChunkBeforeDocEnvelope = true;
		}
	}

	/**
	 * Record (shape only) the first parser error observed across the whole
	 * stream — its chunk index and message — so root-cause analysis can see
	 * where parsing first broke, not just the terminal finish error.
	 */
	private recordFirstParserError(chunkIndex: number, error: unknown): void {
		if (this.lifecycle.firstParserErrorChunkIndex !== undefined) {
			return;
		}
		this.lifecycle.firstParserErrorChunkIndex = chunkIndex;
		this.lifecycle.firstParserErrorMessage = error instanceof Error ? error.message : String(error);
	}

	stream(
		adf: string,
		isFinished: boolean = false,
		traceId: string | undefined,
		singleInstrumentationId?: string,
	): void {
		if (!this.actionId) {
			this.init(traceId, singleInstrumentationId);
		}

		if (!adf || !this.actionId) {
			return;
		}

		const streamDebugEnabled = fg('platform_editor_aifc_create_stream_debug');
		if (streamDebugEnabled) {
			this.updateLifecycleForChunk(adf, this.chunkIndex);
		}
		const debug = streamDebugEnabled
			? withLifecycle(
					withReplay(
						describeCreateChunk({
							actionId: this.actionId,
							traceId,
							chunk: adf,
							chunkIndex: this.chunkIndex,
							accumulatedBefore: this.accumulated,
							isFinished,
							parserPhase: 'stream-process-chunk',
						}),
						this.previousAccumulatedDataReplay,
					),
					this.lifecycle,
					this.commandArrival,
				)
			: undefined;
		if (streamDebugEnabled) {
			this.chunkIndex += 1;
			this.lastCreateStreamDebug = debug;
		}

		if (this.parser) {
			try {
				this.parser.processChunk(adf, isFinished);
			} catch (error) {
				if (error instanceof AdfParseError) {
					// We want to ignore any parsing errors during streaming, but we
					// still capture shape-only debug context (when gated) so a later
					// finish-parse failure can report what the parser observed.
					if (streamDebugEnabled && debug) {
						this.recordFirstParserError(this.chunkIndex - 1, error);
						this.lastCreateStreamDebug = withLifecycle(
							withError(debug, error),
							this.lifecycle,
							this.commandArrival,
						);
					}
				} else {
					// We still want to throw any other unexpected errors
					throw error;
				}
			}
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(
				// NOTE: This is a temporary solution before we move the streamer inside `/platform`
				this.actionId,
				JSON.stringify(this.parser.getPartialDocument()) ?? '',
			);
		} else {
			this.editorApi.aiStreamingOrchestrator?.actions.streamValue(this.actionId, adf);
		}
		if (traceId) {
			this.traceId = traceId;
		}

		this.accumulated += adf;
		log('Streaming content', { actionId: this.actionId, adf });
	}

	async finish({
		onError,
		onRepair,
	}: {
		onError?: StreamErrorCallback;
		onRepair?: StreamRepairCallback;
	}): Promise<DocNode | undefined> {
		if (this.isAborted && fg('platform_editor_ai_fix_create_abort_end_session')) {
			this.isAborted = false;
			this.actionId = undefined;
			this.accumulated = '';
			return undefined;
		}
		if (this.actionId) {
			this.editorApi?.aiStreamingOrchestrator?.actions.finishAction(this.actionId);
			await this.editorApi?.aiStreamingOrchestrator?.actions.endSession();
			log('Finished action', { actionId: this.actionId, fullAdf: this.accumulated });

			let finalDoc: DocNode | undefined;

			try {
				const schema = this.editorApi.core.sharedState.currentState()?.schema;
				if (!schema) {
					throw new Error('Schema is not available');
				}
				const parser = new AdfParser({
					schemaValidator: new AdfSchemaValidator(schema),
					repairStrategy: new AdfRepairStrategy(schema),
				});
				finalDoc = parser.parse(this.accumulated) as DocNode;
				const repairs = parser.getRepairs();
				if (repairs.length > 0) {
					onRepair?.({ repairs, streamType: 'create' });
				}
			} catch (error) {
				// If we fail here record it, and finalDoc is set to `undefined` -> this means that we'll retain
				// whatever we have streamed in, and we don't do any additional work as per the remaining logic
				log('New ADF Parser failed, falling back to ADF Streamer', { error });

				let streamDebug: CreateStreamDebug | undefined;
				if (fg('platform_editor_aifc_create_stream_debug')) {
					// The finish-parse failure is the terminal error; record it as
					// the first parser error too if nothing was captured during
					// streaming.
					this.recordFirstParserError(this.chunkIndex, error);

					const baseDebug =
						this.lastCreateStreamDebug ??
						withReplay(
							describeCreateChunk({
								actionId: this.actionId,
								traceId: this.traceId,
								chunk: '',
								chunkIndex: this.chunkIndex,
								accumulatedBefore: this.accumulated,
								isFinished: true,
								parserPhase: 'finish-parse',
							}),
							this.previousAccumulatedDataReplay,
						);
					streamDebug = withError(
						withLifecycle(
							{
								...baseDebug,
								parserPhase: 'finish-parse',
								accumulatedLengthBefore: this.accumulated.length,
								accumulatedFirstNonWhitespaceCharBefore: firstNonWhitespaceChar(this.accumulated),
								accumulatedStartsWithDocEnvelopeBefore: startsWithDocEnvelope(this.accumulated),
								accumulatedWasEmptyBefore: this.accumulated.length === 0,
								// Replayed === a replay was marked AND at least one chunk
								// was actually streamed for it.
								previousAccumulatedDataReplayed:
									!!this.previousAccumulatedDataReplay && this.lifecycle.receivedChunkCount > 0,
							},
							this.lifecycle,
							this.commandArrival,
						),
						error,
					);
				}

				onError?.({ error, streamType: 'create', traceId: this.traceId, streamDebug });
			}

			this.accumulated = '';
			this.actionId = undefined;
			this.traceId = undefined;
			this.chunkIndex = 0;
			this.lastCreateStreamDebug = undefined;
			this.previousAccumulatedDataReplay = undefined;
			this.lifecycle = createStreamLifecycle();
			this.commandArrival = undefined;
			this.isAborted = false;

			return finalDoc;
		}
	}

	abort(): void {
		this.isAborted = true;
		this.editorApi.aiStreamingOrchestrator?.actions.abortSession();
	}

	/**
	 * Returns the current best-effort parsed ADF from the streaming parser.
	 * Used during streaming to extract media node IDs before the stream completes.
	 */
	getPartialDocument(): unknown {
		return this.parser?.getPartialDocument();
	}
}
